// AIDEV-NOTE: Componente admin para upload/gerenciamento de fotos de um produto.
// Permite upload (max 5), preview, delete, indicador de limite.

import { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  BrokenImage as BrokenImageIcon
} from '@mui/icons-material';
import { useUploadFoto, useDeleteFoto } from '@/hooks/useProdutoFotos';
import type { ProdutoFoto } from '@azoupvendas/shared';

const MAX_FOTOS = 5;
const ACCEPTED_TYPES = 'image/*';

// Sub-componente para exibir uma foto com botão de remover sempre visível
function FotoItem({
  foto,
  onDelete,
  deleteDisabled
}: {
  foto: ProdutoFoto;
  onDelete: () => void;
  deleteDisabled: boolean;
}) {
  const [broken, setBroken] = useState(false);

  return (
    <Box sx={{ position: 'relative' }}>
      {broken ? (
        <Box
          sx={{
            height: 160,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'grey.300',
            gap: 0.5
          }}
        >
          <BrokenImageIcon sx={{ color: 'grey.400', fontSize: 36 }} />
          <Typography variant="caption" color="text.secondary">
            Imagem inválida
          </Typography>
        </Box>
      ) : (
        <img
          src={foto.url}
          alt={`Foto ${foto.ordem + 1}`}
          loading="lazy"
          onError={() => setBroken(true)}
          style={{ height: 160, width: '100%', objectFit: 'contain', borderRadius: 4 }}
        />
      )}
      <IconButton
        size="small"
        onClick={onDelete}
        disabled={deleteDisabled}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(0,0,0,0.55)',
          color: 'white',
          '&:hover': { bgcolor: 'error.main' },
          '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.2)' }
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

interface FotoUploaderProps {
  produtoCodigo: number;
  fotos: ProdutoFoto[];
}

export function FotoUploader({ produtoCodigo, fotos }: FotoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadMutation = useUploadFoto(produtoCodigo);
  const deleteMutation = useDeleteFoto(produtoCodigo);

  const canUpload = fotos.length < MAX_FOTOS;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadError(null);

    // Upload files sequentially to avoid race conditions on count check
    for (let i = 0; i < files.length; i++) {
      if (fotos.length + i >= MAX_FOTOS) {
        setUploadError(`Limite de ${MAX_FOTOS} fotos atingido`);
        break;
      }
      const file = files[i];
      if (!file) continue;
      try {
        await uploadMutation.mutateAsync(file);
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'response' in err
              ? String(
                  (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? err
                )
              : String(err);
        setUploadError(message);
        break;
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fotoId: string) => {
    try {
      await deleteMutation.mutateAsync(fotoId);
    } catch {
      // Error handled by TanStack Query
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {fotos.length}/{MAX_FOTOS} fotos
        </Typography>

        <Button
          variant="contained"
          size="small"
          startIcon={uploadMutation.isPending ? <CircularProgress size={16} /> : <UploadIcon />}
          disabled={!canUpload || uploadMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          Enviar foto
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {fotos.length === 0 && (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography color="text.secondary">
            Nenhuma foto. Clique em &quot;Enviar foto&quot; para adicionar.
          </Typography>
        </Box>
      )}

      {fotos.length > 0 && (
        <ImageList cols={3} gap={8} sx={{ mt: 0 }}>
          {fotos.map((foto) => (
            <ImageListItem key={foto.id} sx={{ position: 'relative' }}>
              <FotoItem
                foto={foto}
                onDelete={() => handleDelete(foto.id)}
                deleteDisabled={deleteMutation.isPending}
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}
    </Box>
  );
}
